import { ListService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { filter } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AssetDetailsDto,
  AssetService,
  AssetTransferableParts,
  IRecheckEligibilityDto,
} from '@proxy/register-service/assets';
import { LocationDto, LocationService } from '@proxy/register-service/locations';
import { DeallocationType } from '@register/proxy/register-service/assets';
import { LocationDefinition } from '@register/proxy/register-service/locations/location-type.enum';
import { QueueItemDto } from '@proxy/queue-service';

interface IEligibleAssetDisplay {
  primaryID?: string;
  secondaryID?: string;
  barcode?: string;
  categoryNickname?: string;
  primaryIdPrefix?: string | null;
}

@Component({
  selector: 'app-move-modal',
  templateUrl: './move-modal.component.html',
  styleUrls: ['./move-modal.component.scss'],
})
export class MoveModalComponent implements OnChanges, OnInit {
  @Input() openMoveModal: boolean = false;
  @Input() asset: AssetDetailsDto | null = null;
  @Input() showMessage = false;
  @Input() twoStepEnabled = false;
  @Input() transferData: AssetTransferableParts = {};
  @Input() assetIds: string[] = [];
  @Input() selectedAssets: QueueItemDto[] = [];
  @Output() modalClose = new EventEmitter<boolean>();
  @Output() openTransferModal = new EventEmitter();

  DeallocationType = DeallocationType;
  selectedLocation: LocationDto | null = null;
  locationDefinition: LocationDefinition = LocationDefinition.HOME;
  isBusy: boolean = true;
  notes = '';
  applyRecheck = false;
  showRecheckPanel = false;
  showApplyRecheckOption = false;
  eligibleAssetDetails: IEligibleAssetDisplay[] = [];
  private pendingMove = false;

  get hasTransfer(): boolean {
    return !!(this.transferData && Object.keys(this.transferData).length > 0);
  }

  get isSingleAsset(): boolean {
    return !!(this.asset || this.selectedAssets.length === 1);
  }

  constructor(
    public readonly list: ListService,
    public readonly service: LocationService,
    public readonly assetService: AssetService,
    public readonly toasterService: ToasterService,
    public readonly confirmation: ConfirmationService,
  ) {}

  ngOnInit(): void {
    const assetIds = this.asset?.id ? [this.asset.id] : this.assetIds;
    this.assetService.CheckRecheckEligibility(assetIds).subscribe({
      next: (results: IRecheckEligibilityDto[]) => {
        const displayIndices = results
          .map((r, i) => (r.isCategoryEligible && r.isFromReCheckLocation ? i : -1))
          .filter(i => i >= 0);

        if (displayIndices.length === 0) return;

        if (this.asset) {
          this.eligibleAssetDetails = [
            {
              primaryID: this.asset.primaryID,
              secondaryID: this.asset.secondaryID,
              barcode: this.asset.barcode,
              categoryNickname: this.asset.categoryNickname,
              primaryIdPrefix: this.asset.primaryIdPrefix,
            },
          ];
        } else {
          const displayIds = displayIndices.map(i => assetIds[i]);
          const displayQueueItems = this.selectedAssets.filter(a => displayIds.includes(a.assetId));
          this.eligibleAssetDetails = displayQueueItems.map(a => ({
            primaryID: a.assetPrimaryID,
            secondaryID: a.assetSecondaryID,
            barcode: a.assetBarcode,
            categoryNickname: a.categoryNickname,
            primaryIdPrefix: a.assetPrimaryIDPrefix,
          }));
        }

        this.showRecheckPanel = true;
        const anyFullyEligible = results.some(r => r.isEligible);
        if (!anyFullyEligible) {
          this.applyRecheck = false;
        }
        this.showApplyRecheckOption = anyFullyEligible;
      },
      error: () => {},
    });
  }

  selectLocation(location: LocationDto) {
    this.isBusy = false;
    this.selectedLocation = location;
  }

  private executeMove(executeReCheckRelease?: boolean) {
    const move = this.assetService.performAction({
      actionType: 'move',
      asset: this.asset,
      assetIds: this.assetIds,
      isQueue: !this.asset,
      transferData: this.transferData,
      selectedLocation: this.selectedLocation,
      transferNotes: this.notes,
      executeReCheckRelease,
    });

    if (!move) {
      this.isBusy = false;
      return;
    }

    move.subscribe({
      next: () => {
        this.isBusy = false;
        this.showRecheckPanel = false;
        this.applyRecheck = false;
        this.showApplyRecheckOption = false;
        this.eligibleAssetDetails = [];
        this.modalClose.emit(true);
        this.toasterService.success('RegisterService::Successful');
      },
      error: () => {
        this.isBusy = false;
      },
    });
  }

  move() {
    if (this.twoStepEnabled && this.asset?.hasSubAssets && !this.hasTransfer) {
      this.pendingMove = true;
      this.openTransferModal.emit();
    } else if (
      (this.isSingleAsset && this.showApplyRecheckOption) ||
      (!this.isSingleAsset && this.applyRecheck)
    ) {
      this.confirmation
        .info('RegisterService::ConfirmRecheckReleaseMessage', 'RegisterService::RecheckRelease', {
          cancelText: 'No',
        })
        .subscribe((status: Confirmation.Status) => {
          this.isBusy = true;
          if (status !== Confirmation.Status.confirm) {
            this.executeMove(false);
          } else {
            this.executeMove(true);
          }
        });
    } else {
      this.isBusy = true;
      this.executeMove(this.applyRecheck);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['transferData'] &&
      this.pendingMove &&
      this.transferData &&
      Object.keys(this.transferData).length > 0
    ) {
      this.pendingMove = false;
      this.isBusy = true;
      this.executeMove(this.applyRecheck);
    }
  }
}
